import React, { useContext, useState, useEffect } from "react";
import {
  Stack,
  Box,
  Grid2 as Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import LinearProgress from "@mui/material/LinearProgress";
import { useMutation } from "@apollo/client";
import { useMutation as mutationDeleteImage } from "react-query";
//Icons
import { BiMinusCircle } from "react-icons/bi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

//Srcs
import "../../Style/dialogStyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { translateLauguage } from "../../Function/Translate";
import { CREATE_SCHOOL, UPDATE_SCHOOL } from "../../Schema/School";

export default function SchoolForm({
  open,
  handleClose,
  setRefetch,
  dialogTitle,
  editData,
}) {
  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  //======================== Create User =========================
  const [createSchool] = useMutation(CREATE_SCHOOL, {
    onCompleted: ({ createSchool }) => {
      // console.log("createSchool::", createSchool);
      setLoading(false);
      if (createSchool?.is_success === true) {
        setAlert(true, "success", createSchool?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createSchool?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });
  //======================== Create User =========================
  const [updateSchool] = useMutation(UPDATE_SCHOOL, {
    onCompleted: ({ updateSchool }) => {
      // console.log("updateSchool::", updateSchool);
      setLoading(false);
      if (updateSchool?.is_success === true) {
        setAlert(true, "success", updateSchool?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateSchool?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    remark: Yup.string(),
    school_name: Yup.string().required(t("require")),
    school_name_en: Yup.string().required(t("require")),
  });

  const formik = useFormik({
    initialValues: {
      remark: "",
      school_name: "",
      school_name_en: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (submitValues) => {
      // console.log("submitValues:::", submitValues);
      setLoading(true);
      if (dialogTitle === "Create") {
        createSchool({
          variables: {
            input: {
              ...submitValues,
            },
          },
        });
      } else {
        updateSchool({
          variables: {
            id: editData?._id,
            input: {
              ...submitValues,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (dialogTitle === "Create") {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if(editData && dialogTitle === "Update"){
      setFieldValue("school_name",editData?.school_name)
      setFieldValue("school_name_en",editData?.school_name_en)
      setFieldValue("remark",editData?.remark)
    }
  },[editData])

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {dialogTitle === "Create"
                ? t("dialog_title_create_school")
                : t("dialog_title_update_school")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_school_name")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_school_name")}
                  {...getFieldProps("school_name")}
                  error={Boolean(touched.school_name && errors.school_name)}
                  helperText={touched.school_name && errors.school_name}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("label_school_name_en")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("label_school_name_en")}
                  {...getFieldProps("school_name_en")}
                  error={Boolean(
                    touched.school_name_en && errors.school_name_en
                  )}
                  helperText={touched.school_name_en && errors.school_name_en}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("remark")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder={t("remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
            >
               {dialogTitle === "Create"
                ? t("btn-create")
                : t("btn-update")}
              
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
